import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { E53StratumData } from './useE53StratumData';

// Extended interface with fileId for mutation operations
interface E53StratumMutationData extends E53StratumData {
  fileId?: number;
}

/**
 * Hook for updating an existing eNO1 stratum
 * @returns Mutation for updating eNO1 stratum data
 */
export function useUpdateE53Stratum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stratumData: E53StratumMutationData) => {
      // Update to use '/ipc/stratum/e53' without the ID in the URL path
      const response = await axios.put('/ipc/stratum/e53', stratumData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Get the fileId from the response or fallback to a property in variables
      const fileId = data?.fileId || variables?.fileId;
      
      // Invalidate queries to trigger a refetch
      if (fileId) {
        queryClient.invalidateQueries({ 
          queryKey: ['eno1StratumData', fileId]
        });
        
        // Also invalidate sample data as stratum changes might affect them
        queryClient.invalidateQueries({ 
          queryKey: ['eno1Samples', fileId]
        });
      }
    }
  });
}

/**
 * Hook for adding a new eNO1 stratum
 * @returns Mutation for adding a new eNO1 stratum
 */
export function useAddENO1Stratum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stratumData: Omit<E53StratumData, 'stratumId'> & { fileId: number }) => {
      const response = await axios.post('/ipc/stratum/e53', stratumData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Get the fileId from the response or fallback to a property in variables
      const fileId = data?.fileId || variables?.fileId;
      
      // Invalidate queries to trigger a refetch
      if (fileId) {
        queryClient.invalidateQueries({ 
          queryKey: ['e53StratumData', fileId]
        });
        
        // Also invalidate sample data as new stratum might affect them
        queryClient.invalidateQueries({ 
          queryKey: ['e53Samples', fileId]
        });
      }
    }
  });
} 